import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { getRowMaxNegotiationRound } from '../utils/helpers.js';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { updateOffersBatch } from '../services/realtimeOfferService.js';
import { usePersistedDataStore } from '../../../store.js';
import { toast } from '../../../../common/toast';
import { createOfferContext } from '../../../../utils/offerContextUtils';
import logger from '../../../../utils/logger';

const EditableNotesComponent = ({ 
  negotiations, 
  loading = false,
  parcelId,
  onUpdateSuccess,
  townView = false,
  notesDisabledReason = null,
  isActive = true,
}) => {
  const notesDisabled = Boolean(notesDisabledReason);
  const markersInactive = notesDisabled || !isActive;
  const INACTIVE_TEXT_COLOR = 'rgba(0, 0, 0, 0.38)';
  const disabledMarkerSx = {
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    opacity: 0.85,
    pointerEvents: 'none',
    cursor: 'not-allowed',
    '&:hover': {},
  };
  const [editingParty, setEditingParty] = useState(null); // 'Aventine' or 'Town'
  const [editingValue, setEditingValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const inputRef = useRef(null);
  const isTypingRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);

  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);

  // Get negotiations data from global state instead of props to ensure updates are reflected
  const globalNegotiations = getNegotiationObj?.cases?.find(caseItem => caseItem.parcel_id === parcelId)?.Negotiations;
  const negotiationsData = globalNegotiations || negotiations;
  const currentRound = useMemo(
    () => getRowMaxNegotiationRound(negotiationsData) || 1,
    [negotiationsData]
  );

  const roundData = negotiationsData?.find(n => n.round === currentRound);
  const aventineOffer = roundData?.offers?.find(offer => offer.OfferedBy === 'Aventine');
  const townOffer = roundData?.offers?.find(offer => offer.OfferedBy === 'Town');
  const hasAventineComment = aventineOffer?.Comments && aventineOffer.Comments.trim() !== '';
  const hasTownComment = townOffer?.Comments && townOffer.Comments.trim() !== '';


  const focusTextarea = useCallback((el) => {
    if (!el) return;
    el.focus({ preventScroll: true });
    const len = el.value?.length ?? 0;
    try {
      el.setSelectionRange(len, len);
    } catch {
      el.selectionStart = el.selectionEnd = len;
    }
  }, []);

  const setTextareaRef = useCallback(
    (node) => {
      inputRef.current = node;
      if (node) {
        requestAnimationFrame(() => focusTextarea(node));
      }
    },
    [focusTextarea],
  );

  const handleEditClick = useCallback((party, evt) => {
    if (markersInactive) return;
    if (townView && party === 'Aventine') return;
    evt.preventDefault();
    evt.stopPropagation();
    setEditingParty(party);
    const offer = party === 'Aventine' ? aventineOffer : townOffer;
    setEditingValue(offer?.Comments || '');

    const cell = evt.currentTarget.closest('[role="cell"]');
    setAnchorEl(cell || evt.currentTarget);
    setEditorOpen(true);
  }, [aventineOffer, townOffer, townView, markersInactive]);

  const saveNote = useCallback(
    async (party, value) => {
      if (isSavingRef.current) return;

      const negotiationObj = usePersistedDataStore.getState().negotiationObj;
      const caseData = negotiationObj?.cases?.find((c) => c.parcel_id === parcelId);
      const round = getRowMaxNegotiationRound(caseData?.Negotiations) || currentRound;
      const roundData = caseData?.Negotiations?.find((n) => n.round === round);
      const offer = roundData?.offers?.find((o) => o.OfferedBy === party);
      if (!offer) return;

      const trimmedValue = value.trim();
      const existing = (offer.Comments ?? '').trim();
      if (trimmedValue === existing) return;

      isSavingRef.current = true;
      setIsUpdating(true);

      try {
        const user = {
          userId: negotiationObj?.UserId,
          userAccess: negotiationObj?.UserAccess?.level || 'Aventine',
        };
        const context = {
          ...createOfferContext(user, negotiationObj),
          ...(townView ? { townView: true } : {}),
        };

        const offerForBatch = {
          parcelId,
          offerValue: offer.Offer ?? null,
          offeredBy: party,
          offerDate: new Date().toISOString(),
          comments: trimmedValue,
          offerId: offer.OfferID,
        };

        await updateOffersBatch([offerForBatch], context, {
          updateFrontend: true,
          round,
        });

        onUpdateSuccess?.();
      } catch (error) {
        logger.error(`Error updating ${party} note:`, error);
        toast.error(`Error updating ${party} note: ${error.message}`);
      } finally {
        isSavingRef.current = false;
        setIsUpdating(false);
      }
    },
    [currentRound, parcelId, onUpdateSuccess, townView],
  );

  const debouncedUpdate = useCallback(
    (party, value) => {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => saveNote(party, value), 2000);
    },
    [saveNote],
  );

  const closeEditor = useCallback(() => {
    clearTimeout(debounceTimeoutRef.current);
    const party = editingParty;
    const value = inputRef.current?.value ?? editingValue;

    setEditorOpen(false);
    setEditingParty(null);
    setEditingValue('');

    if (party) {
      void saveNote(party, value);
    }
  }, [editingParty, editingValue, saveNote]);

  const handleInputChange = useCallback((event) => {
    isTypingRef.current = true;
    const value = event.target.value;
    setEditingValue(value);
    debouncedUpdate(editingParty, value);
    
    // Clear the typing flag after a short delay
    setTimeout(() => {
      isTypingRef.current = false;
    }, 100);
  }, [editingParty, debouncedUpdate]);


  // Measure cell size for initial editor size
  useEffect(() => {
    if (editorOpen && anchorEl) {
      // anchorEl should already be the cell, but just in case
      const cell = anchorEl.getAttribute('role') === 'cell' ? anchorEl : anchorEl.closest?.('[role="cell"]');
      if (cell) {
        setAnchorRect(cell.getBoundingClientRect());
      }
    }
  }, [editorOpen, anchorEl]);

  // Focus once Popper + textarea are mounted (callback ref handles most cases; this is a fallback)
  useLayoutEffect(() => {
    if (!editorOpen || !editingParty) return;
    const el = inputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusTextarea(el));
    });
  }, [editorOpen, editingParty, focusTextarea]);

  // Maintain focus during typing to prevent DataGrid from stealing it
  useEffect(() => {
    const maintainFocus = () => {
      if (editingParty && inputRef.current && isTypingRef.current) {
        // Only refocus if we're currently typing and the input is not focused
        if (document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    // Check focus more frequently during editing
    const interval = editingParty ? setInterval(maintainFocus, 50) : null;
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [editingParty]);

  // Prevent DataGrid keyboard navigation when input is focused
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      if (editingParty && inputRef.current && document.activeElement === inputRef.current) {
        // Prevent DataGrid navigation keys from working when input is focused
        const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'Enter', 'Tab'];
        if (navigationKeys.includes(event.key)) {
          event.stopPropagation();
        }
      }
    };

    if (editingParty) {
      document.addEventListener('keydown', handleGlobalKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [editingParty]);

  // Early returns for loading and data validation
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        height: '100%'
      }}>
        <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!negotiations || !Array.isArray(negotiations)) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        height: '100%',
        minHeight: '32px',
      }}>
        <Typography variant="body2" sx={{ color: '#888' }}>
          No negotiations data
        </Typography>
      </Box>
    );
  }

  if (!roundData || !roundData.offers) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        height: '100%',
      }}>
        <Typography variant="body2" sx={{ color: '#888' }}>
          No data for round {currentRound}
        </Typography>
      </Box>
    );
  }

  const renderPartyMarker = (party, offer, hasComment) => {
    const isAventine = party === 'Aventine';
    const aventineReadOnlyTown = townView && isAventine;
    const partyReadOnly = markersInactive || aventineReadOnlyTown;

    const offerExists = !!offer;

    const noOfferTooltip = 'No offer exists for this round, make an offer to add a note.';
    const noCommentTooltip = isAventine ? 'No Aventine note available' : 'No Town note available';
    const readOnlyTooltip = markersInactive
      ? (notesDisabledReason || 'Case is not active')
      : null;
    const tooltipText = readOnlyTooltip
      ?? (!offerExists
        ? noOfferTooltip
        : partyReadOnly
          ? hasComment ? `${party}: ${offer.Comments}` : noCommentTooltip
          : hasComment ? `${party}: ${offer.Comments}` : `Click to add ${party} note`);

    const inactiveLabelSx = {
      fontSize: '10px',
      fontWeight: 'bold',
      color: INACTIVE_TEXT_COLOR,
      lineHeight: 1,
      ...(townView && !isAventine ? { minWidth: 36, textAlign: 'center' } : {}),
    };

    const markerBoxSx = markersInactive ? {
      alignItems: 'center',
      gap: 0.5,
      padding: '2px 6px',
      boxSizing: 'border-box',
      borderRadius: '4px',
      ...(townView ? { width: '100%', maxWidth: '100%' } : {}),
      display: 'flex',
      justifyContent: 'center',
      ...(townView && !isAventine ? { minWidth: 0 } : {}),
      ...disabledMarkerSx,
    } : {
      alignItems: 'center',
      gap: 0.5,
      padding: '2px 6px',
      boxSizing: 'border-box',
      ...(townView ? { width: '100%', maxWidth: '100%' } : {}),
      borderRadius: '4px',
      backgroundColor: offerExists 
        ? (hasComment ? (isAventine ? '#e8f5e8' : '#e3f2fd') : '#f5f5f5')
        : '#f0f0f0',
      border: offerExists 
        ? `1px solid ${hasComment ? (isAventine ? '#4caf50' : '#2196f3') : '#ddd'}`
        : '1px solid #ccc',
      cursor: offerExists && !partyReadOnly ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      opacity: offerExists ? 1 : 0.6,
      '&:hover': offerExists && !partyReadOnly ? {
        backgroundColor: isAventine ? '#d4edda' : '#bbdefb',
      } : {},
      ...(aventineReadOnlyTown
        ? {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minWidth: 0,
            gap: 0,
            paddingInline: '22px',
          }
        : { display: 'flex', justifyContent: 'center', ...(townView ? { minWidth: 0 } : {}) }),
    };

    const townAveSideSlotSx = {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 18,
      height: '100%',
      pointerEvents: 'none',
    };

    const typographySx = {
      fontSize: '10px',
      fontWeight: 'bold',
      color: offerExists
        ? (hasComment ? (isAventine ? '#2e7d32' : '#1565c0') : '#666')
        : '#999',
      lineHeight: 1,
    };

    let markerChildren;
    if (markersInactive) {
      markerChildren = (
        <Typography variant="caption" sx={inactiveLabelSx}>
          {isAventine ? 'Ave' : 'Town'}
        </Typography>
      );
    } else if (aventineReadOnlyTown) {
      const aveLabelColor = offerExists ? (hasComment ? '#2e7d32' : '#666') : '#999';
      const aveLabel = (
        <Typography
          variant="caption"
          sx={{
            ...typographySx,
            textAlign: 'center',
            color: aveLabelColor,
          }}
        >
          Ave
        </Typography>
      );
      const hiddenAdd = (
        <AddIcon sx={{ fontSize: '12px', visibility: 'hidden', pointerEvents: 'none' }} aria-hidden />
      );
      if (offerExists && hasComment) {
        markerChildren = (
          <>
            <Box sx={{ ...townAveSideSlotSx, left: 4 }} aria-hidden>
              <CheckIcon sx={{ fontSize: '12px', color: '#4caf50', visibility: 'hidden' }} aria-hidden />
            </Box>
            {aveLabel}
            <Box sx={{ ...townAveSideSlotSx, right: 4 }}>
              <CheckIcon sx={{ fontSize: '12px', color: '#4caf50' }} />
            </Box>
          </>
        );
      } else {
        markerChildren = (
          <>
            <Box sx={{ ...townAveSideSlotSx, left: 4 }} aria-hidden>{hiddenAdd}</Box>
            {aveLabel}
            <Box sx={{ ...townAveSideSlotSx, right: 4 }} aria-hidden>{hiddenAdd}</Box>
          </>
        );
      }
    } else {
      markerChildren = (
        <>
          <Typography variant="caption" sx={{
            ...typographySx,
            ...(townView && !isAventine ? { minWidth: 36, textAlign: 'center' } : {}),
          }}
          >
            {isAventine ? 'Ave' : 'Town'}
          </Typography>
          {offerExists ? (
            hasComment ? (
              <CheckIcon sx={{ fontSize: '12px', color: isAventine ? '#4caf50' : '#2196f3' }} />
            ) : (
              <AddIcon sx={{ fontSize: '12px', color: '#999' }} />
            )
          ) : (
            <AddIcon sx={{ fontSize: '12px', color: '#ccc' }} />
          )}
        </>
      );
    }
    
    return (
      <Tooltip
        title={tooltipText}
        open={editorOpen ? false : undefined}
        slotProps={{
          popper: {
            modifiers: [
              { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8, altAxis: true } },
              { name: 'flip', options: { fallbackPlacements: ['top', 'bottom', 'left', 'right'] } },
            ],
          },
          tooltip: {
            sx: {
              maxWidth: 'min(280px, calc(100vw - 16px))',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              fontSize: '12px',
              lineHeight: 1.4,
              padding: '8px 12px',
            },
          },
        }}
      >
        <Box
          sx={markerBoxSx}
          aria-disabled={markersInactive || undefined}
          onMouseDown={
            offerExists && !partyReadOnly
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }
              : undefined
          }
          onClick={offerExists && !partyReadOnly ? (e) => handleEditClick(party, e) : undefined}
        >
          {markerChildren}
        </Box>
      </Tooltip>
    );
  };


  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 1, 
        width: '100%',
        height: '100%',
      }}
      >
        {townView ? (
          <>
            <Box sx={{ flex: '1 1 0%', minWidth: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {renderPartyMarker('Aventine', aventineOffer, hasAventineComment)}
            </Box>
            <Box sx={{ flex: '1 1 0%', minWidth: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {renderPartyMarker('Town', townOffer, hasTownComment)}
            </Box>
          </>
        ) : (
          <>
            {renderPartyMarker('Aventine', aventineOffer, hasAventineComment)}
            {renderPartyMarker('Town', townOffer, hasTownComment)}
          </>
        )}
      </Box>

      {/* Floating Editor — mount only when open so autoFocus runs each time */}
      {editorOpen && anchorEl && (
      <Popper
        open
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 6] } },
          { name: 'flip', enabled: true },
          { name: 'preventOverflow', options: { altAxis: true } }
        ]}
      >
        <ClickAwayListener
          onClickAway={(e) => {
            // Don't close if we click the cell or any of its children
            if (anchorEl && anchorEl.contains(e.target)) return;
            closeEditor();
          }}
        >
          <Paper
            elevation={6}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{
              p: 1,
              // Use minimum dimensions and 80% of cell width
              width: anchorRect ? anchorRect.width * 0.8 : 256,
              minWidth: 200,
              minHeight: 40,
              maxWidth: 400,
              maxHeight: 200,
              overflow: 'auto',
              borderRadius: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: editingParty === 'Aventine' ? '#2e7d32' : '#1565c0',
                mb: 0.5,
                lineHeight: 1,
              }}
            >
              {editingParty === 'Aventine' ? 'Aventine' : 'Town'}
            </Typography>

            <TextField
              inputRef={setTextareaRef}
              autoFocus
              multiline
              fullWidth
              placeholder={`Add ${editingParty} note...`}
              value={editingValue}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Escape') {
                  e.preventDefault();
                  closeEditor();
                }
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => {
                // ensure caret-at-end even if browser's autofocus wins the race
                e.stopPropagation();
                const el = e.target;
                const len = el.value?.length ?? 0;
                requestAnimationFrame(() => {
                  try { el.setSelectionRange(len, len); } catch {}
                });
              }}
              variant="outlined"
              minRows={2}
              maxRows={6}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 12,
                  lineHeight: 1.5,
                  minHeight: 32,
                },
                '& textarea': {
                  overflow: 'auto',
                  minHeight: 32,
                  maxHeight: 160,
                },
              }}
            />

            {isUpdating && (
              <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#666', mt: 0.5 }}>
                Saving...
              </Typography>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
      )}
    </>
  );
};

export default EditableNotesComponent;
